import React, { useId } from "react";
import * as styles from "./HeroBannerSlide.module.scss";
import clsx from "clsx";
import {
  SectionContainer,
  ButtonsContainer,
  UnstyledBlockContent
} from "@silversea-ssc/ui-components-react";
import PageBuilderButtonCTA from "components/PageBuilder/PageBuilderButtonCTA";
import NativeHeroImage from "components/NativeHeroImage/NativeHeroImage";
import { blockContentToString } from "src/gatsby/algolia/blockContentToString";
import { HeroBannerSlideshowItem } from "./utils";
import { dateFromOptionalDate } from "src/utils/date";
import { forceResTimezone } from "src/utils/date/forceResTimezone";
import { useLingui } from "@lingui/react/macro";
import { isPresent } from "src/utils/checks";
const Countdown = React.lazy(() => import("components/Countdown/Countdown"));
interface HeroBannerSlideProps {
  slide: HeroBannerSlideshowItem;
  sliderControls: JSX.Element;
  loading?: "lazy" | undefined;
  customLayoutName?: "30-years";
}

const HeroBannerSlide: React.FC<
  React.PropsWithChildren<HeroBannerSlideProps>
> = ({ slide, sliderControls, loading, customLayoutName }) => {
  const { t } = useLingui();
  const id = useId();
  const { _type, title, content, image, ctaButtons } = slide;
  const imageAlt = blockContentToString(title) || "";
  const tokenValues = _type == "promo" ? slide.promo.tokenValues : undefined;
  const maybeCountdownExpireDate =
    _type == "promo" && slide.showCountdown
      ? dateFromOptionalDate(slide.promo.toDate)
      : undefined;

  const showCountdown =
    maybeCountdownExpireDate && maybeCountdownExpireDate > new Date();

  const maybeCountdownLabel =
    _type == "promo" ? slide.countdownLabel : undefined;

  const heroCtaButtons = ctaButtons?.filter(isPresent);

  return (
    <section
      className={styles.slide}
      aria-roledescription="slide"
      aria-labelledby={`heading-${id}`}
    >
      <div
        className={clsx(styles.slideBackground, {
          [styles.slideBackground30th]: customLayoutName == "30-years"
        })}
      >
        {image && (
          <NativeHeroImage
            image={image}
            alt={imageAlt}
            lazyload={loading == "lazy"}
            className={styles.image}
          />
        )}
      </div>

      <React.Fragment>
        <SectionContainer
          className={styles.contentContainer}
          wrapperClassName={styles.contentContainerWrapper}
          backgroundColor="transparent"
        >
          <div
            className={clsx({
              [styles.topContent]: true
            })}
          >
            <h1 className={styles.title} id={`heading-${id}`}>
              <UnstyledBlockContent value={title} tokens={tokenValues} />
            </h1>
            <div className={styles.content}>
              <UnstyledBlockContent value={content} tokens={tokenValues} />
            </div>

            {heroCtaButtons && heroCtaButtons.length > 0 && (
              <div className={styles.ctas}>
                <ButtonsContainer align="left">
                  {showCountdown && (
                    <React.Suspense fallback={null}>
                      <Countdown
                        expireDate={forceResTimezone(maybeCountdownExpireDate)}
                        label={maybeCountdownLabel ?? t`OFFER ENDS IN`}
                      />
                    </React.Suspense>
                  )}
                  {heroCtaButtons.map((b) => (
                    <PageBuilderButtonCTA
                      key={b?._key}
                      {...b}
                      forHero="inverted"
                    />
                  ))}
                </ButtonsContainer>
              </div>
            )}
          </div>
          {sliderControls}
        </SectionContainer>
      </React.Fragment>
    </section>
  );
};

export default React.memo(HeroBannerSlide);
